// webapp/src/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [isAuthInitialized, setIsAuthInitialized] = useState(false);
    const [hasReauthenticated, setHasReauthenticated] = useState(false);
    const [nextPage, setNextPage] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Set up Supabase auth listener
        const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
            setCurrentUser(session?.user || null);
            setIsAuthInitialized(true);
            setLoading(false);
        });

        // Initial session check
        fetchUser();

        // Cleanup subscription
        return () => {
            if (authListener && authListener.subscription) {
                authListener.subscription.unsubscribe();
            }
        };
    }, []);

    async function fetchUser() {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            setCurrentUser(session?.user || null);
            setIsAuthInitialized(true);
            setLoading(false);
        } catch (error) {
            console.error("Error getting session:", error);
            setLoading(false);
        }
    }

    const markUserAsReauthenticated = () => {
        setHasReauthenticated(true);
    };

    const resetReauthenticationStatus = () => {
        setHasReauthenticated(false);
    };

    const setNextPageAfterReauth = (page) => {
        setNextPage(page);
    };

    const getNextPageAfterReauth = () => {
        return nextPage;
    };

    // Function to check premium status through Supabase RLS or custom claims
    const checkPremiumStatus = async () => {
        if (!currentUser) return false;
        
        try {
            // Get premium status from a Supabase function or RPC
            const { data, error } = await supabase.rpc('check_premium_status');
            
            if (error) throw error;
            return data || false;
        } catch (error) {
            console.error("Error checking premium status:", error);
            return false;
        }
    };

    // Conditional rendering based on the loading state
    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <AuthContext.Provider value={{
            currentUser,
            isAuthInitialized,
            hasReauthenticated,
            markUserAsReauthenticated,
            resetReauthenticationStatus,
            setNextPageAfterReauth,
            getNextPageAfterReauth,
            checkPremiumStatus,
        }}>
            {children}
        </AuthContext.Provider>
    );
};